import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { maintenanceItemsApi } from '../buildingManagementApi';
import type { BuildingMaintenanceSettingsDetail } from '../buildingManagementTypes';

const MIN_DAYS = 1;
const MAX_DAYS = 365;

/**
 * The two aging thresholds.
 *
 * **Saving re-ages every item at once** — the bands are arithmetic over these numbers, stored on
 * no item — so the consequence is stated on the form, before the tick.
 *
 * **No Autogenerate.** The form opens already filled with the current values, which is the
 * contract the button exists to meet — the exemption `<ResetUserPasswordModal>` takes.
 */
export function AgingSettingsModal({
  open,
  current,
  onClose,
  onSaved,
}: {
  open: boolean;
  current: BuildingMaintenanceSettingsDetail;
  onClose: () => void;
  onSaved: (next: BuildingMaintenanceSettingsDetail) => void;
}) {
  const toast = useToast();

  const [nearDays, setNearDays] = useState(String(current.nearExpirationDays));
  const [escalationDays, setEscalationDays] = useState(String(current.overdueEscalationDays));

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const near = parseDays(nearDays);
  const escalation = parseDays(escalationDays);

  const canSave = near !== null && escalation !== null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const next = await maintenanceItemsApi.updateSettings({
        nearExpirationDays: near,
        overdueEscalationDays: escalation,
      });

      toast.success('Saved the aging settings. Every item is aged against them now.');
      onSaved(next);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the settings.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'aging-settings' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit aging"
      size="lg"
      id="aging-settings-modal"
      confirm={{
        id: 'aging-settings-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="aging-settings-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="aging-settings-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="aging-settings-near-expiration-days"
            label="Near Expiration window (days)"
            required
            type="number"
            inputMode="numeric"
            min={MIN_DAYS}
            max={MAX_DAYS}
            className="font-mono"
            description="How many days before its due date an item reads as Near Expiration — for every category that sets no window of its own."
            value={nearDays}
            onChange={(e) => setNearDays(e.target.value)}
            disabled={saving}
            error={
              nearDays.trim() !== '' && near === null
                ? `Give a whole number between ${MIN_DAYS} and ${MAX_DAYS}.`
                : error?.fieldError('nearExpirationDays')
            }
          />

          <TextField
            id="aging-settings-overdue-escalation-days"
            label="Escalation threshold (days)"
            required
            type="number"
            inputMode="numeric"
            min={MIN_DAYS}
            max={MAX_DAYS}
            className="font-mono"
            description={`How many days past its due date an Expired item moves to the escalated band. At ${escalation ?? 'N'} it is labelled "${escalation ?? 'N'} Days Expired".`}
            value={escalationDays}
            onChange={(e) => setEscalationDays(e.target.value)}
            disabled={saving}
            error={
              escalationDays.trim() !== '' && escalation === null
                ? `Give a whole number between ${MIN_DAYS} and ${MAX_DAYS}.`
                : error?.fieldError('overdueEscalationDays')
            }
          />
        </div>

        <p className="text-xs text-slate-600">
          Every item in the register is re-aged against these numbers the moment you submit. A
          category&apos;s own Near Expiration window still wins over the first.
        </p>
      </div>
    </Modal>
  );
}

function parseDays(raw: string): number | null {
  if (raw.trim() === '') return null;

  const value = Number(raw);

  return Number.isInteger(value) && value >= MIN_DAYS && value <= MAX_DAYS ? value : null;
}
